import React, { useState, useMemo, useEffect } from "react";
import "./PayFlow.css";
import { useAuth } from "../hooks/useAuth";
import {
  usePayflowStore,
  payflowActions,
  refreshFromServer,
  computeRow,
  confirmedOutMinutes,
  exceptionLabel,
  partiesInPeriod,
  barIssuesInPeriod,
  type BarCandidate,
  isHiddenAccount,
  rowKey,
  fmtTime,
  fmtTimeOrNone,
  fmtHours,
  fmtMoney,
  minsToInputValue,
  inputValueToMins,
  suggestedWorkedTimes,
  type RowStatus,
  type ManualPoolGroup,
} from "./payflowStore";

/**
 * PayFlow — Manager review (user side).
 *
 * Two kinds of task:
 *   1. Clock-in/out exceptions the admin sheet put on hold (early in / late out
 *      beyond tolerance) — the manager confirms the worked times.
 *   2. One "manual pool required?" task per period at the tips stage. Normal
 *      pooling is automatic (by bar location), so the manager only has to flag
 *      ad-hoc pools (e.g. a group-buyout) — answer No to close it, or Yes to
 *      create named pools of the staff who worked.
 *
 * Layout follows SMRT Tasks: one task per row, View to expand. Reads/writes the
 * shared localStorage-backed payflowStore.
 */

type FilterKey = "pending" | "confirmed";

// Friendly date+time for a stored ISO timestamp (matches the admin audit log).
const fmtStamp = (iso?: string): string => (iso ? new Date(iso).toLocaleString() : "");

// A minute count as a human duration: under an hour stays "45 min"; an hour or
// more reads "9 hours and 5 minutes" (the minutes part is dropped when it's 0).
const fmtDuration = (mins: number): string => {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const hPart = `${h} hour${h === 1 ? "" : "s"}`;
  return m === 0 ? hPart : `${hPart} and ${m} minute${m === 1 ? "" : "s"}`;
};

interface ExceptionItem {
  key: string;
  periodName: string;
  weekday: string;
  dateLabel: string;
  name: string;
  role: string;
  status: RowStatus;
  reason: string;
  inException: boolean;
  outException: boolean;
  // Worked with no schedule / scheduled with no clock record. Tasks in their own right:
  // the manager authorises the unscheduled shift after the fact, or says whether the
  // scheduled one was worked (a forgotten clock-in) or not worked at all.
  unscheduled: boolean;
  unworked: boolean;
  earlyInMins: number;
  lateOutMins: number;
  // The missing side of an unmatched row: absent, and shown as "—". A manager is never
  // asked to confirm times against a window the sources did not give.
  schedStart?: number;
  schedEnd?: number;
  actualIn?: number;
  effectiveOut?: number;
  confirmedIn?: number;
  confirmedOut?: number;
  confirmedBy?: string;
  confirmedAt?: string;
  netHours: number;
}

interface Draft {
  in: string;
  out: string;
  reason: string;
}

interface StaffMember {
  id: string;
  name: string;
  role: string;
}

// One task per Event-account party (an account on a given day). The manager splits that
// party's pool — all auto-grat — across role-band groups by percentage.
interface ManualPoolTask {
  key: string; // partyKey: `${dateISO}|Event N`
  periodId: string;
  periodName: string;
  eventName: string; // "Event 2"
  dateLabel: string; // "Fri, Aug 15"
  pool: number; // the pool to distribute (net CC tips after pool+CC, plus auto-grats)
  status: "pending" | "confirmed";
  resolvedBy?: string;
  resolvedAt?: string;
  groups: ManualPoolGroup[];
  staff: StaffMember[]; // real staff who worked that day (group candidates)
}

// One task per server-to-bar amount charged in a window with no Main Bar pool to receive
// it. The manager allocates it — keep with the servers, or send it to a bar that worked
// that window — which unblocks Verify Tips.
interface BarTask {
  key: string; // `${periodId}:${dateISO}|${window}` — unique for the list and expand state
  issueKey: string; // `${dateISO}|${window}` — the decision key
  periodId: string;
  periodName: string;
  dateLabel: string; // "Fri, Aug 15"
  window: "day" | "night";
  amount: number;
  candidates: BarCandidate[]; // bartenders/managers on duty that day — the manager picks from these
  status: "pending" | "confirmed";
  recipients?: string[]; // the allocated recipient ids once resolved ([] = nobody)
  resolvedBy?: string;
  resolvedAt?: string;
}

export default function PayFlow() {
  const {
    payPeriods,
    hydrated,
    syncError,
    adjustments,
    reviews,
    // The live break rules, used only as the fallback below: this screen spans every
    // pay period at once, so each row is computed under its own period's snapshot.
    breakRules: liveBreakRules,
    hoursApproved,
    tipsApproved,
    manualPools,
    barAllocations,
  } = usePayflowStore();
  const { user } = useAuth();
  const currentUser: string = user?.name || user?.email || "Unknown user";
  const [filter, setFilter] = useState<FilterKey>("pending");
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [menuOpen, setMenuOpen] = useState<Record<string, boolean>>({});
  // Per bar-allocation task → which candidate ids the manager has ticked.
  const [barSelected, setBarSelected] = useState<Record<string, Record<string, boolean>>>({});

  // Manual-pool modal state — scoped to one party.
  const [manualTask, setManualTask] = useState<ManualPoolTask | null>(null);
  const [draftGroups, setDraftGroups] = useState<ManualPoolGroup[]>([]);
  const [groupName, setGroupName] = useState("");
  const [groupPct, setGroupPct] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  // Opening the screen pulls the payroll list fresh; the day-by-day rows every
  // task is derived from arrive per payroll, so ask for the ones still missing.
  useEffect(() => {
    void refreshFromServer();
  }, []);

  useEffect(() => {
    void payflowActions.loadAllPeriodDetails();
  }, [payPeriods]);

  // Every payroll's rows AND decisions have to be in hand before the task list is
  // complete. Without the decisions, a shift a manager already confirmed reads as an
  // outstanding exception and reappears in their queue.
  const detailsPending =
    !hydrated || payPeriods.some((p) => !p.daysLoaded || !p.decisionsLoaded);

  // One task per Event-account party in any period at the tips stage (hours verified,
  // tips not yet). Each party's pool (all auto-grat) is split by the manager across
  // role-band groups; a period with no Event account raises no task.
  const manualTasks = useMemo<ManualPoolTask[]>(() => {
    const items: ManualPoolTask[] = [];
    for (const period of payPeriods) {
      const id = period.id;
      if (!hoursApproved[id] || tipsApproved[id]) continue;
      // Needs the payroll's rows AND its decisions, or a party already built reappears.
      if (!period.daysLoaded || !period.decisionsLoaded) continue;
      const state = manualPools[id];
      for (const party of partiesInPeriod(period)) {
        // Group candidates = the real staff who worked that party's day (not the Event
        // account itself, and not people from other days).
        const day = period.days.find((d) => d.dateISO === party.dateISO);
        const staffMap = new Map<string, StaffMember>();
        for (const r of day?.rows ?? []) {
          if (isHiddenAccount(r.name)) continue;
          if (!staffMap.has(r.id)) staffMap.set(r.id, { id: r.id, name: r.name, role: r.role });
        }
        const staff = [...staffMap.values()].sort((a, b) => a.name.localeCompare(b.name));
        const pool = state?.parties?.[party.key];
        items.push({
          key: party.key,
          periodId: id,
          periodName: period.name,
          eventName: party.eventName,
          dateLabel: `${party.weekday}, ${party.dateLabel}`,
          pool: party.pool,
          status: pool?.resolved ? "confirmed" : "pending",
          resolvedBy: pool?.by,
          resolvedAt: pool?.at,
          groups: pool?.groups ?? [],
          staff,
        });
      }
    }
    return items;
  }, [payPeriods, hoursApproved, tipsApproved, manualPools]);

  // One task per unallocated server-to-bar amount in any period at the tips stage. A period
  // with a Main Bar pool (the normal case) raises none.
  const barTasks = useMemo<BarTask[]>(() => {
    const items: BarTask[] = [];
    for (const period of payPeriods) {
      const id = period.id;
      if (!hoursApproved[id] || tipsApproved[id]) continue;
      if (!period.daysLoaded || !period.decisionsLoaded) continue;
      const resolved = barAllocations[id] ?? {};
      for (const issue of barIssuesInPeriod(period)) {
        const r = resolved[issue.key];
        items.push({
          key: `${id}:${issue.key}`,
          issueKey: issue.key,
          periodId: id,
          periodName: period.name,
          dateLabel: `${issue.weekday}, ${issue.dateLabel}`,
          window: issue.window,
          amount: issue.amount,
          candidates: issue.candidates,
          status: r ? "confirmed" : "pending",
          recipients: r?.recipients,
          resolvedBy: r?.by,
          resolvedAt: r?.at,
        });
      }
    }
    return items;
  }, [payPeriods, hoursApproved, tipsApproved, barAllocations]);

  const exceptions = useMemo<ExceptionItem[]>(() => {
    const items: ExceptionItem[] = [];
    for (const period of payPeriods) {
      for (const day of period.days) {
        for (const r of day.rows) {
          // Event party buckets and the Takeout sales account are not a real
          // clock-in to reconcile, so neither raises an exception.
          if (isHiddenAccount(r.name)) continue;
          const key = rowKey(period.id, day.index, r.id, r.seq);
          // Each period's own break rules, so a rule committed today cannot change
          // which shifts in a finished period look like exceptions.
          const c = computeRow(
            r,
            adjustments[key],
            reviews[key],
            period.rules?.breakRules ?? liveBreakRules,
          );
          if (!c.hasException) continue;
          items.push({
            key,
            periodName: period.name,
            weekday: day.weekday,
            dateLabel: day.dateLabel,
            name: r.name,
            role: r.role,
            status: c.status,
            reason: c.reason,
            inException: c.inException,
            outException: c.outException,
            unscheduled: c.unscheduled,
            unworked: c.unworked,
            earlyInMins: c.earlyInMins,
            lateOutMins: c.lateOutMins,
            schedStart: r.schedStart,
            schedEnd: r.schedEnd,
            actualIn: r.actualIn,
            effectiveOut: c.effectiveOut,
            confirmedIn: c.confirmedIn,
            confirmedOut: c.confirmedOut,
            confirmedBy: c.confirmedBy,
            confirmedAt: c.confirmedAt,
            netHours: c.netHours,
          });
        }
      }
    }
    return items;
  }, [payPeriods, adjustments, reviews, liveBreakRules]);

  const counts = useMemo(
    () => ({
      pending:
        exceptions.filter((e) => e.status === "pending").length +
        manualTasks.filter((t) => t.status === "pending").length +
        barTasks.filter((t) => t.status === "pending").length,
      confirmed:
        exceptions.filter((e) => e.status === "confirmed").length +
        manualTasks.filter((t) => t.status === "confirmed").length +
        barTasks.filter((t) => t.status === "confirmed").length,
    }),
    [exceptions, manualTasks, barTasks],
  );

  const visible = exceptions.filter((e) => e.status === filter);
  const visibleManual = manualTasks.filter((t) => t.status === filter);
  const visibleBar = barTasks.filter((t) => t.status === filter);

  const subjectFor = (t: ManualPoolTask) => `${t.eventName} · ${t.dateLabel}`;

  const reopenManual = (t: ManualPoolTask) =>
    payflowActions.reopenPartyPool(t.periodId, t.key, currentUser, subjectFor(t));

  // ── Manual-pool modal helpers ──
  const draftPctTotal = draftGroups.reduce((s, g) => s + (g.pct || 0), 0);

  const openManualModal = (t: ManualPoolTask) => {
    setManualTask(t);
    setDraftGroups(t.groups);
    setSelected({});
    setGroupName("");
    setGroupPct("");
  };

  const addDraftGroup = () => {
    const memberIds = Object.keys(selected).filter((id) => selected[id]);
    const name = groupName.trim();
    const pct = Math.round(parseFloat(groupPct));
    if (!name || memberIds.length === 0 || !Number.isFinite(pct) || pct <= 0) return;
    setDraftGroups((prev) => [
      ...prev,
      { id: `g${prev.length + 1}-${name}`, name, memberIds, pct },
    ]);
    setSelected({});
    setGroupName("");
    setGroupPct("");
  };

  const removeDraftGroup = (id: string) =>
    setDraftGroups((prev) => prev.filter((g) => g.id !== id));

  const saveManual = () => {
    if (!manualTask || draftGroups.length === 0 || draftPctTotal !== 100) return;
    payflowActions.setPartyPool(
      manualTask.periodId,
      manualTask.key,
      draftGroups,
      currentUser,
      subjectFor(manualTask),
    );
    setManualTask(null);
  };

  // The share of the pool one member of a draft group would receive, for a live preview.
  const memberShare = (g: ManualPoolGroup): number =>
    g.memberIds.length ? (manualTask ? manualTask.pool : 0) * (g.pct / 100) / g.memberIds.length : 0;

  const draftFor = (e: ExceptionItem): Draft => {
    if (drafts[e.key]) return drafts[e.key];
    const sugg = suggestedWorkedTimes({
      schedStart: e.schedStart,
      schedEnd: e.schedEnd,
      actualIn: e.actualIn,
      actualOut: e.effectiveOut,
    });
    return { in: minsToInputValue(sugg.in), out: minsToInputValue(sugg.out), reason: "" };
  };

  const setDraft = (key: string, patch: Partial<Draft>, base: Draft) =>
    setDrafts((prev) => ({ ...prev, [key]: { ...base, ...patch } }));

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleMenu = (key: string) =>
    setMenuOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const confirm = (e: ExceptionItem) => {
    const d = draftFor(e);
    const reason = d.reason.trim();
    if (!reason) return;
    const inMins = inputValueToMins(d.in);
    const outMins = confirmedOutMinutes(inMins, inputValueToMins(d.out));
    payflowActions.confirm(e.key, inMins, outMins, reason, currentUser, `${e.name}, ${e.dateLabel}`);
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[e.key];
      return next;
    });
    setExpanded((prev) => ({ ...prev, [e.key]: false }));
  };

  return (
    <div className="payflow-mgr-page">
      <h1 className="payflow-mgr-title">PayFlow</h1>

      {/* Traffic-light filter: red = needs action, green = completed. */}
      <div className="payflow-mgr-traffic">
        <div
          className={`payflow-mgr-light${filter === "pending" ? " payflow-mgr-light-active" : ""}`}
          title="Pending — needs manager action"
          onClick={() => setFilter("pending")}
        >
          <span className="payflow-mgr-light-dot">🔴</span>
          <span className="payflow-mgr-light-count">{counts.pending}</span>
        </div>

        <div className="payflow-mgr-light payflow-mgr-light-empty" aria-hidden="true">
          <span className="payflow-mgr-light-dot">🟡</span>
        </div>

        <div
          className={`payflow-mgr-light${filter === "confirmed" ? " payflow-mgr-light-active" : ""}`}
          title="Confirmed — completed"
          onClick={() => setFilter("confirmed")}
        >
          <span className="payflow-mgr-light-dot">🟢</span>
          <span className="payflow-mgr-light-count">{counts.confirmed}</span>
        </div>
      </div>

      {visible.length === 0 && visibleManual.length === 0 && visibleBar.length === 0 ? (
        <div className="payflow-mgr-empty">
          {detailsPending
            ? syncError || "Loading tasks…"
            : filter === "pending"
              ? "Nothing awaiting review. You're all caught up. 🎉"
              : "No confirmed tasks yet."}
        </div>
      ) : (
        <div className="payflow-mgr-tasklist">
          {visible.map((e) => {
            const d = draftFor(e);
            const isOpen = !!expanded[e.key];
            const issueLabel = exceptionLabel(e);
            return (
              <div key={e.key} className="payflow-mgr-task">
                {/* Single-line task row */}
                <div
                  className="payflow-mgr-task-row"
                  data-menu-open={menuOpen[e.key] ? "true" : "false"}
                >
                  <div className="payflow-mgr-task-main">
                    <div className="payflow-mgr-task-title">{e.name}</div>
                    <div className="payflow-mgr-task-meta">
                      {e.dateLabel}
                      <span className="payflow-mgr-meta-sep">·</span>
                      {e.role}
                      <span className="payflow-mgr-meta-sep">·</span>
                      {issueLabel}
                    </div>
                  </div>

                  <div className="payflow-mgr-task-actions">
                    <button
                      className="payflow-mgr-btn-icon payflow-mgr-btn-view"
                      title={isOpen ? "Collapse" : "View"}
                      onClick={() => toggleExpanded(e.key)}
                    >
                      👁
                    </button>

                    {e.status === "pending" && (
                      <button
                        className={`payflow-mgr-btn-check${d.reason.trim() ? " payflow-mgr-btn-check-ready" : ""}`}
                        disabled={!d.reason.trim()}
                        title={
                          d.reason.trim() ? "Confirm adjustment" : "Enter a reason below to enable"
                        }
                        onClick={() => confirm(e)}
                      >
                        ✓
                      </button>
                    )}

                    {e.status === "confirmed" && (
                      <>
                        <button
                          className="payflow-mgr-btn-icon payflow-mgr-btn-warning payflow-mgr-btn-secondary"
                          data-label="Re-open"
                          title="Re-open"
                          onClick={() => {
                            payflowActions.reopen(e.key, currentUser, `${e.name}, ${e.dateLabel}`);
                            setMenuOpen((prev) => ({ ...prev, [e.key]: false }));
                          }}
                        >
                          ↺
                        </button>
                        <button
                          className="payflow-mgr-btn-icon payflow-mgr-btn-menu-trigger"
                          title="More actions"
                          onClick={() => toggleMenu(e.key)}
                        >
                          ⋮
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Expanded detail */}
                {isOpen && (
                  <div className="payflow-mgr-task-detail">
                    <div className="payflow-mgr-variances">
                      {e.inException && (
                        <div className="payflow-mgr-variance">
                          <span className="payflow-mgr-variance-tag">Clock-in</span>
                          Clocked in <strong>{fmtDuration(e.earlyInMins)} early</strong>
                        </div>
                      )}
                      {e.outException && (
                        <div className="payflow-mgr-variance">
                          <span className="payflow-mgr-variance-tag">Clock-out</span>
                          Clocked out <strong>{fmtDuration(e.lateOutMins)} late</strong>
                        </div>
                      )}
                      {e.unscheduled && (
                        <div className="payflow-mgr-variance">
                          <span className="payflow-mgr-variance-tag">Unscheduled</span>
                          Worked with <strong>no scheduled shift</strong> — confirm the hours
                          worked if the shift was authorised
                        </div>
                      )}
                      {e.unworked && (
                        <div className="payflow-mgr-variance">
                          <span className="payflow-mgr-variance-tag">Not clocked</span>
                          Scheduled but <strong>never clocked in</strong> — confirm the hours
                          worked, or set In and Out to the same time to record none
                        </div>
                      )}
                    </div>

                    {/* One side of an unmatched shift is missing, and stays missing: the
                        manager's confirmation below is what fills it, on the record. */}
                    <div className="payflow-mgr-times">
                      <div className="payflow-mgr-times-row">
                        <span className="payflow-mgr-times-label">Scheduled</span>
                        <span>{fmtTimeOrNone(e.schedStart)} – {fmtTimeOrNone(e.schedEnd)}</span>
                      </div>
                      <div className="payflow-mgr-times-row">
                        <span className="payflow-mgr-times-label">Actual</span>
                        <span>{fmtTimeOrNone(e.actualIn)} – {fmtTimeOrNone(e.effectiveOut)}</span>
                      </div>
                    </div>

                    {e.status === "pending" ? (
                      <>
                        <textarea
                          className="payflow-mgr-reason"
                          placeholder="Reason for the adjustment (required)…"
                          value={d.reason}
                          onChange={(ev) => setDraft(e.key, { reason: ev.target.value }, d)}
                        />
                        <div className="payflow-mgr-confirm-row">
                          <label className="payflow-mgr-time-field">
                            In
                            <input
                              type="time"
                              className="payflow-mgr-time-input"
                              value={d.in}
                              onChange={(ev) => setDraft(e.key, { in: ev.target.value }, d)}
                            />
                          </label>
                          <label className="payflow-mgr-time-field">
                            Out
                            <input
                              type="time"
                              className="payflow-mgr-time-input"
                              value={d.out}
                              onChange={(ev) => setDraft(e.key, { out: ev.target.value }, d)}
                            />
                          </label>
                        </div>
                      </>
                    ) : (
                      <div className="payflow-mgr-completion">
                        <div className="payflow-mgr-completion-line">
                          <strong>Confirmed by {e.confirmedBy || "Unknown user"}</strong>
                          {e.confirmedIn !== undefined && e.confirmedOut !== undefined
                            ? ` — worked ${fmtTime(e.confirmedIn)} – ${fmtTime(e.confirmedOut)} · Net ${fmtHours(e.netHours)} h`
                            : ""}
                        </div>
                        {e.confirmedAt && (
                          <div className="payflow-mgr-completion-stamp">
                            {fmtStamp(e.confirmedAt)}
                          </div>
                        )}
                        <div className="payflow-mgr-completion-reason">
                          <strong>Reason:</strong> {e.reason}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {visibleManual.map((t) => {
            const isOpen = !!expanded[t.key];
            return (
              <div key={t.key} className="payflow-mgr-task">
                <div
                  className="payflow-mgr-task-row"
                  data-menu-open={menuOpen[t.key] ? "true" : "false"}
                >
                  <div className="payflow-mgr-task-main">
                    <div className="payflow-mgr-task-title">Manual Pool — {t.eventName}</div>
                    <div className="payflow-mgr-task-meta">
                      {t.dateLabel}
                      <span className="payflow-mgr-meta-sep">·</span>
                      {fmtMoney(t.pool)} to split
                    </div>
                  </div>
                  <div className="payflow-mgr-task-actions">
                    <button
                      className="payflow-mgr-btn-icon payflow-mgr-btn-view"
                      title={isOpen ? "Collapse" : "View"}
                      onClick={() => toggleExpanded(t.key)}
                    >
                      👁
                    </button>

                    {t.status === "confirmed" && (
                      <>
                        <button
                          className="payflow-mgr-btn-icon payflow-mgr-btn-warning payflow-mgr-btn-secondary"
                          data-label="Re-open"
                          title="Re-open"
                          onClick={() => {
                            reopenManual(t);
                            setMenuOpen((prev) => ({ ...prev, [t.key]: false }));
                          }}
                        >
                          ↺
                        </button>
                        <button
                          className="payflow-mgr-btn-icon payflow-mgr-btn-menu-trigger"
                          title="More actions"
                          onClick={() => toggleMenu(t.key)}
                        >
                          ⋮
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="payflow-mgr-task-detail">
                    {t.status === "pending" ? (
                      <>
                        <p className="payflow-mgr-prompt">
                          A <strong>{t.eventName}</strong> party booking with{" "}
                          <strong>{fmtMoney(t.pool)}</strong> to split. Group the staff who
                          worked it by role and give each group a share of the pool.
                        </p>
                        <div className="payflow-mgr-pool-actions">
                          <button
                            className="payflow-mgr-btn payflow-mgr-btn-confirm"
                            onClick={() => openManualModal(t)}
                          >
                            {t.groups.length ? "Edit pool" : "Build pool"}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="payflow-mgr-completion">
                        <div className="payflow-mgr-completion-line">
                          <strong>Built by {t.resolvedBy || "Unknown user"}</strong> —{" "}
                          {fmtMoney(t.pool)} across {t.groups.length} group
                          {t.groups.length === 1 ? "" : "s"}
                        </div>
                        {t.resolvedAt && (
                          <div className="payflow-mgr-completion-stamp">{fmtStamp(t.resolvedAt)}</div>
                        )}
                        {t.groups.map((g) => (
                          <div key={g.id} className="payflow-mgr-completion-reason">
                            <strong>{g.name}</strong> ({g.pct}%):{" "}
                            {g.memberIds
                              .map((id) => t.staff.find((s) => s.id === id)?.name || id)
                              .join(", ")}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {visibleBar.map((t) => {
            const isOpen = !!expanded[t.key];
            const windowLabel = t.window === "day" ? "Day (before 5 PM)" : "Night (after 5 PM)";
            // Ticked candidates: the manager's working selection, seeded from any existing
            // allocation the first time the task is opened.
            const sel =
              barSelected[t.key] ??
              Object.fromEntries((t.recipients ?? []).map((id) => [id, true]));
            const toggle = (id: string) =>
              setBarSelected((prev) => ({
                ...prev,
                [t.key]: { ...sel, [id]: !sel[id] },
              }));
            const chosen = t.candidates.filter((c) => sel[c.id]).map((c) => c.id);
            const complete = () =>
              payflowActions.setBarAllocation(
                t.periodId,
                t.issueKey,
                chosen,
                currentUser,
                `${t.dateLabel} · ${windowLabel}`,
              );
            const nameFor = (id: string) =>
              t.candidates.find((c) => c.id === id)?.name || id;
            return (
              <div key={t.key} className="payflow-mgr-task">
                <div
                  className="payflow-mgr-task-row"
                  data-menu-open={menuOpen[t.key] ? "true" : "false"}
                >
                  <div className="payflow-mgr-task-main">
                    <div className="payflow-mgr-task-title">
                      Bar Allocation — {fmtMoney(t.amount)}
                    </div>
                    <div className="payflow-mgr-task-meta">
                      {t.dateLabel}
                      <span className="payflow-mgr-meta-sep">·</span>
                      {windowLabel}
                    </div>
                  </div>
                  <div className="payflow-mgr-task-actions">
                    <button
                      className="payflow-mgr-btn-icon payflow-mgr-btn-view"
                      title={isOpen ? "Collapse" : "View"}
                      onClick={() => toggleExpanded(t.key)}
                    >
                      👁
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="payflow-mgr-task-detail">
                    {t.status === "confirmed" && (
                      <div className="payflow-mgr-completion-line">
                        <strong>Allocated by {t.resolvedBy || "Unknown user"}</strong> —{" "}
                        {(t.recipients ?? []).length === 0
                          ? "no one (servers paid no tip-out)"
                          : (t.recipients ?? []).map(nameFor).join(", ")}
                        {t.resolvedAt ? ` · ${fmtStamp(t.resolvedAt)}` : ""}
                      </div>
                    )}
                    <p className="payflow-mgr-prompt">
                      <strong>{fmtMoney(t.amount)}</strong> of server-to-bar tip-out was charged
                      on {t.dateLabel} ({windowLabel}), but no bartender was on Main Bar. Tick who
                      should receive it — one, several, or no one (tick nobody, and the servers
                      pay no tip-out) — then mark it complete.
                    </p>
                    {t.candidates.length === 0 ? (
                      <p className="payflow-mgr-prompt">
                        No bartenders or managers were on duty that day. Marking complete with no
                        one means the servers pay no tip-out.
                      </p>
                    ) : (
                      <div className="payflow-mgr-edit-list">
                        {t.candidates.map((c) => (
                          <label key={c.id} className="payflow-mgr-edit-row">
                            <input
                              type="checkbox"
                              checked={!!sel[c.id]}
                              onChange={() => toggle(c.id)}
                            />
                            {c.name} <span className="payflow-mgr-edit-role">· {c.role}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    <div className="payflow-mgr-pool-actions">
                      <button className="payflow-mgr-btn payflow-mgr-btn-confirm" onClick={complete}>
                        {chosen.length === 0
                          ? "Mark complete — no one"
                          : `Give to ${chosen.length} ${chosen.length === 1 ? "person" : "people"}`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Manual-pool builder — one party */}
      {manualTask && (
        <div className="payflow-mgr-modal-overlay" onClick={() => setManualTask(null)}>
          <div className="payflow-mgr-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="payflow-mgr-modal-title">
              {manualTask.eventName} — {fmtMoney(manualTask.pool)}
            </h2>
            <p className="payflow-mgr-modal-sub">
              {manualTask.dateLabel}. Make a group per role (e.g. Bartenders, Servers, Kitchen),
              tick who was in it, and give each a percentage of the pool. The percentages must
              total 100%; each group's share splits evenly among its members.
            </p>

            {draftGroups.length > 0 && (
              <div className="payflow-mgr-pool-members">
                {draftGroups.map((g) => (
                  <span key={g.id} className="payflow-mgr-chip">
                    {g.name} · {g.pct}% · {g.memberIds.length} staff
                    {g.memberIds.length > 0 ? ` (${fmtMoney(memberShare(g))} each)` : ""}
                    <button
                      className="payflow-mgr-chip-x"
                      title="Remove group"
                      onClick={() => removeDraftGroup(g.id)}
                    >
                      ✕
                    </button>
                  </span>
                ))}
                <div
                  className={`payflow-mgr-pct-total${
                    draftPctTotal === 100 ? " payflow-mgr-pct-ok" : ""
                  }`}
                >
                  Allocated: {draftPctTotal}% {draftPctTotal === 100 ? "✓" : "(must total 100%)"}
                </div>
              </div>
            )}

            <div className="payflow-mgr-edit-list">
              {manualTask.staff.map((s) => (
                <label key={s.id} className="payflow-mgr-edit-row">
                  <input
                    type="checkbox"
                    checked={!!selected[s.id]}
                    onChange={() => setSelected((prev) => ({ ...prev, [s.id]: !prev[s.id] }))}
                  />
                  {s.name} <span className="payflow-mgr-edit-role">· {s.role}</span>
                </label>
              ))}
            </div>

            <div className="payflow-mgr-confirm-row">
              <input
                type="text"
                className="payflow-mgr-time-input payflow-mgr-pool-name"
                placeholder="Group (e.g. Bartenders)"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
              <input
                type="number"
                min={1}
                max={100}
                className="payflow-mgr-time-input payflow-mgr-pct-input"
                placeholder="%"
                value={groupPct}
                onChange={(e) => setGroupPct(e.target.value)}
              />
              <button
                className="payflow-mgr-btn payflow-mgr-btn-edit"
                disabled={
                  !groupName.trim() ||
                  !Object.values(selected).some(Boolean) ||
                  !(parseFloat(groupPct) > 0)
                }
                onClick={addDraftGroup}
              >
                Add group
              </button>
            </div>

            <div className="payflow-mgr-edit-actions">
              <button
                className="payflow-mgr-btn payflow-mgr-btn-edit"
                onClick={() => setManualTask(null)}
              >
                Cancel
              </button>
              <button
                className="payflow-mgr-btn payflow-mgr-btn-confirm"
                disabled={draftGroups.length === 0 || draftPctTotal !== 100}
                title={
                  draftPctTotal !== 100 ? "Group percentages must total 100%" : "Save this pool"
                }
                onClick={saveManual}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
